// timestamp/time are audited exceptions to the At/Time/Date suffix pattern.
const DATE_KEY_SUFFIX_PATTERN = /(At|Time|Date|CheckIn)$/
const DATE_KEY_EXACT_NAMES = new Set(['timestamp', 'time'])
const MIN_PLAUSIBLE_EPOCH_SECONDS = 1_000_000_000 // 2001-09-09
const MAX_PLAUSIBLE_EPOCH_SECONDS = 7_258_118_400 // 2200-01-01

// Fields the schemas declare as arbitrary (z.record/z.unknown/.passthrough)
// — never walked, so business data can't coincidentally look date-shaped.
const OPAQUE_KEYS = new Set([
  'filters',
  'args',
  'workload',
  'minSpecifications',
  'action',
])

// Known numeric fields that match the suffix pattern but aren't timestamps
// — clip offsets and a network stat, both in milliseconds. inTime/outTime on
// a long recording can exceed MIN_PLAUSIBLE_EPOCH_SECONDS and get misread.
const NON_DATE_KEYS = new Set(['inTime', 'outTime', 'roundTripTime'])

const looksLikeDateKey = (key: string): boolean =>
  !NON_DATE_KEYS.has(key) &&
  (DATE_KEY_SUFFIX_PATTERN.test(key) || DATE_KEY_EXACT_NAMES.has(key))

const isPlausibleEpochSeconds = (value: number): boolean =>
  Number.isFinite(value) &&
  value >= MIN_PLAUSIBLE_EPOCH_SECONDS &&
  value <= MAX_PLAUSIBLE_EPOCH_SECONDS

/**
 * TEMPORARY (ab/iso-datetime, media-library-api#423): coerces epoch-seconds
 * numbers under date-shaped keys to ISO strings. Does not otherwise validate
 * the response shape.
 */
export function coerceEpochTimestamps(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(coerceEpochTimestamps)
  }

  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value)) {
      if (OPAQUE_KEYS.has(key)) {
        result[key] = val
        continue
      }
      result[key] =
        typeof val === 'number' &&
        looksLikeDateKey(key) &&
        isPlausibleEpochSeconds(val)
          ? new Date(val * 1000).toISOString()
          : coerceEpochTimestamps(val)
    }
    return result
  }

  return value
}
